import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightbox — full-screen image viewer with prev/next nav.
 * Props:
 *   images: string[]
 *   index: number  (current index)
 *   onClose: () => void
 *   onChange: (newIndex: number) => void
 */
export const Lightbox = ({ images, index, onClose, onChange }) => {
  const open = index !== null && index !== undefined && index >= 0;

  const goPrev = useCallback(() => {
    if (!open) return;
    onChange((index - 1 + images.length) % images.length);
  }, [open, images, index, onChange]);

  const goNext = useCallback(() => {
    if (!open) return;
    onChange((index + 1) % images.length);
  }, [open, images, index, onChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 md:top-8 md:right-8 z-[110] w-12 h-12 rounded-full border-2 border-white/40 text-white hover:border-[color:var(--punk-red)] hover:text-[color:var(--punk-red)] flex items-center justify-center transition-colors"
        aria-label="Close"
        data-testid="lightbox-close-btn"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <span
          className="absolute top-7 left-5 md:top-10 md:left-8 z-[110] font-typewriter text-xs md:text-sm uppercase tracking-[0.3em] text-white/70 pointer-events-none"
          data-testid="lightbox-counter"
        >
          {String(index + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
        </span>
      )}

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/40 text-white hover:border-[color:var(--punk-red)] hover:text-[color:var(--punk-red)] flex items-center justify-center transition-colors"
          aria-label="Previous image"
          data-testid="lightbox-prev-btn"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/40 text-white hover:border-[color:var(--punk-red)] hover:text-[color:var(--punk-red)] flex items-center justify-center transition-colors"
          aria-label="Next image"
          data-testid="lightbox-next-btn"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      {/* Image */}
      <img
        src={images[index]}
        alt={`Frame ${index + 1}`}
        className="max-w-[92vw] max-h-[88vh] object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        draggable={false}
      />
    </div>
  );
};
